// src/components/InventoryHub.js
import React, { useEffect, useMemo, useState } from "react";
import Character from "./Character";
import { motion, AnimatePresence } from "framer-motion";
import "../index.css";
import { getSkinSrc } from "./SeasonalSkins";
import DefenseDeck from "./DefenseDeck";
import CardPicker from "./CardPicker";
import CardMerge from "./CardMerge";
import ItemsInventory from "./ItemsInventory";
import LoadingSpinner from "./LoadingSpinner";
import { getNameStyleCSS } from "./GameNameStyles";

const API_BASE = process.env.REACT_APP_API_URL;
const PREVIEW_LIMIT = 3;
const FALLBACK_DECK_SIZE = 3;
const OVERALL_DECK_KEY = "overall";

function safeParseJson(value, fallback) {
  if (value == null) return fallback;
  if (typeof value === "object") return value;

  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

function rarityRank(rarity) {
  const r = String(rarity || "common").toLowerCase();
  if (r === "legendary") return 4;
  if (r === "epic") return 3;
  if (r === "rare") return 2;
  return 1;
}

function parseAbilities(raw) {
  const parsed = safeParseJson(raw, []);
  return Array.isArray(parsed) ? parsed : [];
}

function sanitizeAbilityList(list) {
  return (Array.isArray(list) ? list : [])
    .map((ability) => {
      if (typeof ability === "string") return ability;
      return ability?.name || "";
    })
    .map((ability) => String(ability || "").trim())
    .filter(Boolean);
}

function cardPower(card) {
  return (
    Number(card?.attack || 0) +
    Number(card?.defense || 0) +
    Number(card?.health || 0) +
    Number(card?.level || 1) * 2 +
    rarityRank(card?.rarity) * 5
  );
}

function getCardCharacter(card) {
  const parsed = safeParseJson(card?.character, {});
  return parsed && typeof parsed === "object" ? parsed : {};
}

function getCardSide(card) {
  const character = getCardCharacter(card);

  return String(
    character?.background?.side ||
    character?.body?.side ||
    card?.side ||
    ""
  )
    .trim()
    .toLowerCase();
}

function getUserVisualSide(user) {
  const side = String(user?.side || "")
    .trim()
    .toLowerCase();

  return side === "bad" || side === "moon"
    ? "moon"
    : "sun";
}

function normalizeItemType(type) {
  const s = String(type ?? "").trim().toLowerCase();

    if ([
    "trophyshield",
    "trophy_shield",
    "trophy-shield"
  ].includes(s)) {
    return "trophyShield";
  }

  if (["cardpieces", "cardpiece", "card_pieces", "card-pieces"].includes(s)) {
    return "cardPieces";
  }

  if (["name", "cosmetic", "name_cosmetic"].includes(s)) {
    return "name";
  }

  if (["rune", "runes"].includes(s)) return "runes";
  if (["spark", "sparks"].includes(s)) return "sparks";
  if (["booster", "boosters"].includes(s)) return "booster";
  if (["magic_potion", "magic-potion", "magicpotion", "potion"].includes(s)) return "magic_potion";
  if (["ability", "abilities"].includes(s)) return "ability";
  if (["seasonalaccessory", "seasonal_accessory", "seasonal-accessory", "skin", "skins"].includes(s)) {
    return "seasonal_accessory";
  }

  return s;
}

function normalizeChestKey(key) {
  const s = String(key ?? "").trim().toLowerCase();
  if (s.includes("wood")) return "wood";
  if (s.includes("silver")) return "silver";
  if (s.includes("gold")) return "golden";
  if (s.includes("diamond")) return "diamond";
  return "wood";
}

const CHESTS_META = {
  wood: { label: "Dustbound", image: "shop_chest_5.png" },
  silver: { label: "Cursed Tomb", image: "shop_chest_3.png" },
  golden: { label: "Lunar Horn", image: "shop_chest_1.png" },
  diamond: { label: "Solar Horizon", image: "shop_chest_2.png" },
};

function getChestMetaFromChestType(chestType) {
  return CHESTS_META[normalizeChestKey(chestType)];
}

function chestTypeToDisplay(type) {
  const s = String(type || "").toLowerCase();
  if (s.includes("legendary")) return "Legendary Chest";
  if (s.includes("epic")) return "Epic Chest";
  if (s.includes("rare")) return "Rare Chest";
  if (s.includes("common")) return "Common Chest";
  if (s.includes("abilities")) return "Ability Chest";
  if (s.includes("boosters")) return "Booster Chest";
  if (s.includes("skins")) return "Skin Chest";
  return "Chest";
}

function getItemName(inv) {
  const item = inv?.item || {};
  const type = normalizeItemType(item.type);

  if (type === "chest") {
  // Chest antigo das Arenas
  if (inv?.chest?.id) {
    const chestType =
      inv?.chest?.chestType ??
      item?.chest_type ??
      item?.chestType ??
      "";

    const meta = getChestMetaFromChestType(chestType);

    return meta?.label
      ? `${meta.label} Chest`
      : chestTypeToDisplay(chestType);
  }

  // Chest criado em Admin > Items
  return (
    item?.display_name ||
    item?.name ||
    "Chest"
  );
}

  if (type === "cardPieces") {
    return item.card_name || item.cardName || inv.card_name || inv.cardName || "Card Pieces";
  }

  if (type === "sparks") return `${Number(item.sparks_amount || inv.amount || 0).toLocaleString()} Sparks`;
  if (type === "runes") return `${Number(item.runes_amount || inv.amount || 0).toLocaleString()} Runes`;
  if (type === "ability" && item.ability_name) return item.ability_name;
  if (type === "booster" && item.booster_multiplier) return `${item.booster_multiplier}x Booster`;
  if (type === "magic_potion") return item.display_name || item.name || "Magic Potion";

  return item.display_name || item.name || item.accessory_name || item.ability_name || "Item";
}

function normalizeRarity(rarity) {
  const r = String(rarity || "").trim().toLowerCase();

  if (r === "legendary") return "legendary";
  if (r === "epic") return "epic";
  if (r === "rare") return "rare";

  return "common";
}

function rarityToIcon(rarity) {
  switch (normalizeRarity(rarity)) {
    case "legendary":
      return "/legendary.png";

    case "epic":
      return "/epic.png";

    case "rare":
      return "/rare.png";

    case "common":
    default:
      return "/common.png";
  }
}

function getCardRarityFromInv(inv) {
  const item = inv?.item || {};

  const directRarity =
    item.card_rarity ??
    item.cardRarity ??
    item.rarity ??
    item.card?.rarity ??
    inv?.card_rarity ??
    inv?.cardRarity ??
    inv?.rarity;

  if (directRarity) {
    return directRarity;
  }

  const itemName = String(
    item.display_name ||
    item.name ||
    ""
  ).toLowerCase();

  if (itemName.includes("legendary")) return "legendary";
  if (itemName.includes("epic")) return "epic";
  if (itemName.includes("rare")) return "rare";
  if (itemName.includes("common")) return "common";

  return "common";
}

function getItemIconSrc(inv) {
  const item = inv?.item || {};

  const rawType = String(
    item?.type ||
    inv?.type ||
    ""
  )
    .trim()
    .toLowerCase();

  const itemName = String(
    item?.display_name ||
    item?.displayName ||
    item?.name ||
    item?.item_name ||
    inv?.display_name ||
    inv?.name ||
    ""
  )
    .trim()
    .toLowerCase();

  /*
   * IMPORTANTE:
   * Alguns itens podem chegar com type incorreto ou genérico.
   * Por isso, detectamos Spins antes de verificar Card.
   */
  const isSpin =
    [
      "spin",
      "spins",
      "fortune_spin",
      "fortune_spins",
      "fortune-wheel",
      "fortune_wheel",
    ].includes(rawType) ||
    itemName.includes("spin") ||
    itemName.includes("fortune");

  if (isSpin) {
    return "/fortuneicon.png";
  }

  const type = normalizeItemType(rawType);

  switch (type) {
    case "runes":
      return "/runes.png";

    case "sparks":
      return "/sparks.png";

    case "booster":
      return "/energy.png";

    case "tapi":
      return "/icon_tapi.png";

      case "magic_potion":
  return "/magic_potion.png";

    case "evolve":
      return "/evolveicon.png";

    case "seasonal_accessory":
      return "/fragment.png";

    case "shield":
      return "/shield.png";

    case "ability":
      return "/fightlog.png";

    case "gamepass":
      return "/passicon.png";

    case "cardPieces":
      return "/pieces.png";

        case "card":
      return rarityToIcon(getCardRarityFromInv(inv));

    case "bundle": {
      const customImage =
        item?.image_url ||
        item?.imageUrl;

      return customImage || "/pack.png";
    }

    case "name": {
      const nameCategory = String(
        item?.name_category ||
        item?.nameCategory ||
        ""
      )
        .trim()
        .toLowerCase();

      const cosmeticValue = String(
        item?.name_value ||
        item?.nameValue ||
        item?.image ||
        item?.icon ||
        ""
      ).trim();

      if (cosmeticValue) {
        if (
          cosmeticValue.startsWith("/") ||
          cosmeticValue.startsWith("http://") ||
          cosmeticValue.startsWith("https://")
        ) {
          return cosmeticValue;
        }

        return `/name-cosmetics/${cosmeticValue}`;
      }

      if (
        nameCategory === "banners" ||
        nameCategory === "banner"
      ) {
        return "/banner_icon.png";
      }

      if (
        nameCategory === "emogy" ||
        nameCategory === "emoji" ||
        nameCategory === "emojy"
      ) {
        return "/emogy_icon.png";
      }

      return "/style_icon.png";
    }

    case "chest": {
  // Chest criado em Admin > Items
  if (!inv?.chest?.id) {
    const customImage =
      item?.image_url ||
      item?.imageUrl;

    if (customImage) {
      return customImage;
    }

    return "/pack.png";
  }

  // Chest antigo das Arenas
  const chestType =
    inv?.chest?.chestType ??
    item?.chest_type ??
    item?.chestType ??
    "";

  const meta = getChestMetaFromChestType(chestType);

    return meta?.image
    ? `/${meta.image}`
    : "/pack.png";
}

    default:
      return "/pack.png";
  }
}

function buildChestRows(chests) {
  return (Array.isArray(chests) ? chests : []).map((chest) => ({
    user_item_id: `chest:${chest.id}`,
    quantity: 1,
    claimed: false,
    chest,
    item: {
      type: "chest",
      display_name: chestTypeToDisplay(chest.chestType),
      chest_type: chest.chestType,
      unlock_at: chest.unlockAt,
      created_at: chest.createdAt,
    },
  }));
}

const CHEST_REWARD_META = {
  sparks: { label: "Sparks", icon: "/sparks.png" },
  runes: { label: "Runes", icon: "/runes.png" },
  cardPieces: { label: "Card Pieces", icon: "/pieces.png" },
  cardpieces: { label: "Card Pieces", icon: "/pieces.png" },
    spins: { label: "Spins", icon: "/fortuneicon.png" },
  trophyShield: { label: "Trophy Shield", icon: "/shield.png" },
  shield: { label: "Trophy Shield", icon: "/shield.png" },
  tapi: { label: "TAPI", icon: "/icon_tapi.png" },
  evolve: { label: "Evolve", icon: "/evolveicon.png" },
  booster: { label: "Booster", icon: "/energy.png" },
  boosters: { label: "Booster", icon: "/energy.png" },
  ability: { label: "Ability", icon: "/fightlog.png" },
  abilities: { label: "Ability", icon: "/fightlog.png" },
  skins: { label: "Skin", icon: "/fragment.png" },
  seasonal_accessory: { label: "Skin", icon: "/fragment.png" },
  common: { label: "Common Card", icon: "/common.png" },
  rare: { label: "Rare Card", icon: "/rare.png" },
  epic: { label: "Epic Card", icon: "/epic.png" },
  legendary: { label: "Legendary Card", icon: "/legendary.png" },
};

function mapChestRewardsToRows(openResult) {
  const rewards =
    openResult?.rewards ||
    openResult?.reward ||
    openResult?.items ||
    {};

  if (Array.isArray(rewards)) {
    return rewards
      .map((reward, idx) => {
        const rawType =
          reward?.type ||
          reward?.itemType ||
          reward?.rewardType ||
          reward?.key ||
          "";

        const type = normalizeItemType(rawType);
        const meta = CHEST_REWARD_META[type] ||
          CHEST_REWARD_META[String(type || "").toLowerCase()] || {
            label: reward?.label || reward?.name || type || "Reward",
            icon: reward?.icon || "/pack.png",
          };

        return {
          id: `${type || "reward"}_${idx}`,
          type,
          label: reward?.label || reward?.name || meta.label,
          icon: reward?.icon || meta.icon,
          amount:
            Number(
              reward?.amount ??
                reward?.quantity ??
                reward?.qty ??
                reward?.value ??
                1
            ) || 1,
          extra:
            reward?.cardName ||
            reward?.card_name ||
            reward?.name ||
            "",
        };
      })
      .filter((reward) => reward.amount > 0);
  }

  return Object.entries(rewards || {})
    .map(([rawType, rawAmount]) => {
      const type = normalizeItemType(rawType);
      const meta = CHEST_REWARD_META[type] ||
        CHEST_REWARD_META[String(type || "").toLowerCase()] || {
          label: rawType,
          icon: "/pack.png",
        };

      const extra =
        type === "cardPieces"
          ? openResult?.cardPiecesCardName ||
            openResult?.cardPiecesCardId ||
            ""
          : "";

      return {
        id: `${type}_${Math.random().toString(16).slice(2)}`,
        type,
        label: meta.label,
        icon: meta.icon,
        amount: Number(rawAmount) || 0,
        extra,
      };
    })
    .filter((reward) => reward.amount > 0);
}

function SectionCard({ title, subtitle, actionLabel, onClick, children }) {
  return (
    <section style={{marginLeft: -10, marginRight: -10}}>

        <div className="modal_title" style={{paddingLeft: 28, boxSizing: "border-box", textAlign: "left", marginTop: 0}}>
        {title} ({subtitle})       

        <button
          type="button"
          onClick={onClick}
          className="modal_bt"
        >
          {actionLabel}
        </button>

        </div>

        

      <div className="modal_body modal_body_2">
      {children}
      </div>
    </section>
  );
}

function MiniCard({ card, emptyLabel = "Empty", onClick }) {
  const character = getCardCharacter(card);
  const abilitiesSafe = sanitizeAbilityList(parseAbilities(card?.abilities));

  if (!card) {
    return (
      <div style={{
        aspectRatio: 0.8,
      backgroundImage: 'url("/v2/nocard.png"), url("/v2/item.png")',
          backgroundRepeat: "no-repeat, no-repeat",
          backgroundSize: "55%, 100% 100%",
          backgroundPosition: "center center, center center",
    padding: 0,
    paddingBottom: 6,
    position: "relative",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    cursor: "pointer"}}>

    <div className="game-button-small yellow-button">
          EMPTY
        </div>
      </div>
    );
  }

  return (
    <div
  onClick={() => onClick?.(card)}
  role="button"
  tabIndex={0}
  style={{
    backgroundImage: 'url("/v2/item.png")',
    padding: 3,
    backgroundRepeat: "repeat",
    backgroundSize: "100% 100%",
    backgroundPosition: "center center",
    position: "relative",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    cursor: "pointer",
    overflow: "hidden",
  }}
>
      <div
  style={{
    float: "left",
    width: "100%",
    height: "calc(100% - 24px)",
    borderRadius: 4,
    cursor: "default",
    position: "relative",
    overflow: "hidden",
  }}
>

<div
  style={{
    position: "absolute",
    top: 6,
    right: 5,
    zIndex: 30,
    display: "flex",
    flexDirection: "column",
    gap: 3,
    pointerEvents: "none",
  }}
>
  {abilitiesSafe.map((ability, idx) => {
    const abilityKey = String(ability || "").toLowerCase();

    return (
      <div
        key={`${ability}-${idx}`}
        style={{
          width: 15,
          height: 15,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        title={ability}
      >
        <img
          src={`/a_${abilityKey}.png`}
          alt={ability}
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
          style={{
            width: 15,
            height: 15,
            objectFit: "contain",
            pointerEvents: "none",
          }}
        />
      </div>
    );
  })}
</div>


        
        <div style={{   position: "absolute",
  top: 6,
  left: 5,
  width: 28,
  height: 28,
  backgroundImage: "url('/v2/position.png')",
  backgroundSize: "100% 100%",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "center",
  color: "#fff",
  fontSize: 15,
  lineHeight: "28px",
  textAlign: "center",
  zIndex: 20,
  fontFamily: '"Fredoka", sans-serif',
  fontWeight: 600}}>
          {card.level ?? 1}
        </div>

          <div
  className="char_bg"
  style={{
    position: "relative",
    width: "100%",
    aspectRatio: 1,
    zIndex: 2,
  }}
>

<div style={{zIndex: 20,
    textTransform: "uppercase",
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "100%",
    color: "#fff",
    height: 18,
    fontSize: 11,
    lineHeight: "21px",
    textAlign: "center",
    fontWeight: 600,
    background: "rgba(77, 44, 36, 0.5)"}}>
          {card.rarity || "common"}
        </div>

  {Object.entries(character)
    .sort(([a], [b]) => {
      const order = [
        "background",
        "body",
        "boots",
        "pants",
        "shirt",
        "neck",
        "face",
        "eyes",
        "mouth",
        "head",
        "weapons",
      ];

      const indexA = order.includes(a) ? order.indexOf(a) : order.length;
      const indexB = order.includes(b) ? order.indexOf(b) : order.length;

      return indexA - indexB;
    })
    .map(([part, data]) => {
      if (!data || typeof data !== "object") return null;

      const defaultSrc = `/assets/${part}/${data.side}/${data.rarity}/${data.variant}_thumb.png`;

      const src = getSkinSrc(
        card?.seasonal_accessory?.season_name || card?.season_name || null,
        part,
        defaultSrc
      );

      const zIndexMap = {
        background: 1,
        body: 2,
        boots: 3,
        pants: 4,
        shirt: 5,
        neck: 6,
        face: 7,
        eyes: 8,
        mouth: 9,
        head: 10,
        weapons: 11,
      };

      return (
        <img
          key={part}
          src={src}
          alt=""
          onError={(e) => {
            console.log("Missing mini card asset:", src);
            e.currentTarget.style.display = "none";
          }}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "contain",
            zIndex: zIndexMap[part] ?? 10,
            pointerEvents: "none",
          }}
        />
      );
    })}
</div>



      </div>

<div
  style={{
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 4,
    padding: "3px 4px",
    boxSizing: "border-box",
    height: 24
  }}
>
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 2,
      color: "#fff",
      fontSize: 12,
      fontWeight: 500,
      textShadow: "1px 1px #000",
    }}
  >
    <img
      src="/s_attack.png"
      alt=""
      style={{
        width: 12,
        height: 12,
        objectFit: "contain",
      }}
    />
    {card.attack ?? 0}
  </div>

  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 2,
      color: "#fff",
      fontSize: 12,
      fontWeight: 500,
      textShadow: "1px 1px #000",
    }}
  >
    <img
      src="/s_defense.png"
      alt=""
      style={{
        width: 12,
        height: 12,
        objectFit: "contain",
      }}
    />
    {card.defense ?? 0}
  </div>

  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 2,
      color: "#fff",
      fontSize: 12,
      fontWeight: 500,
      textShadow: "1px 1px #000",
    }}
  >
    <img
      src="/s_health.png"
      alt=""
      style={{
        width: 12,
        height: 12,
        objectFit: "contain",
      }}
    />
    {card.health ?? 0}
  </div>
</div>

    </div>
  );
}


function isNameStyleItem(inv) {
  const item = inv?.item || {};
  const type = normalizeItemType(item.type);
  const category = String(
    item?.name_category || item?.nameCategory || ""
  )
    .trim()
    .toLowerCase();

  return (
    type === "name" &&
    (category === "styles" || category === "style")
  );
}

function NameStyleSample({ inv }) {
  const styleValue = String(
    inv?.item?.name_value ||
      inv?.item?.nameValue ||
      ""
  ).trim();

  return (
    <div
      style={{
        width: "100%",
        height: 72,
        marginTop: -12,
        marginBottom: -7,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "0 5px",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          maxWidth: "100%",
          fontSize: 18,
          lineHeight: 1.1,
          fontWeight: 800,
          textAlign: "center",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          ...getNameStyleCSS(styleValue),
        }}
      >
        Player Name
      </div>
    </div>
  );
}

function getItemIconStyle(inv, size = 45) {
  const item = inv?.item || {};
  const type = normalizeItemType(item.type);

  const nameCategory = String(
    item?.name_category || item?.nameCategory || ""
  )
    .trim()
    .toLowerCase();

  if (type === "name") {
    if (nameCategory === "styles" || nameCategory === "style") {
      return {
        width: 72,
        height: 72,
        maxWidth: 72,
        maxHeight: 72,
        marginTop: -12,
        marginBottom: -7,
      };
    }

    if (
      nameCategory === "emogy" ||
      nameCategory === "emoji" ||
      nameCategory === "emojy"
    ) {
      return {
        width: 46,
        height: 46,
        maxWidth: 46,
        maxHeight: 46,
        marginTop: 0,
        marginBottom: 5,
      };
    }

    if (
      nameCategory === "banners" ||
      nameCategory === "banner"
    ) {
      return {
        width: 90,
        height: 90,
        maxWidth: 90,
        maxHeight: 90,
        marginTop: -21,
        marginBottom: -16,
      };
    }
  }

  return {
    width: size,
    height: size,
    maxWidth: size,
    maxHeight: size,
    marginTop: 0,
    marginBottom: 5,
  };
}

function isNameCosmetic(inv) {
  return normalizeItemType(inv?.item?.type) === "name";
}

function getNameCosmeticColumn(inv) {
  const category = String(
    inv?.item?.name_category ||
      inv?.item?.nameCategory ||
      ""
  )
    .trim()
    .toLowerCase();

  if (category === "styles" || category === "style") {
    return "active_name_style";
  }

  if (category === "banners" || category === "banner") {
    return "active_name_banner";
  }

  if (
    category === "emogy" ||
    category === "emoji"
  ) {
    return "active_name_emogy";
  }

  return null;
}

function getNameCosmeticValue(inv) {
  return String(
    inv?.item?.name_value ||
      inv?.item?.nameValue ||
      ""
  ).trim();
}

function isNameCosmeticActive(inv, user) {
  const column = getNameCosmeticColumn(inv);

  if (!column) {
    return false;
  }

  const activeValue = String(user?.[column] || "").trim();
  const itemValue = getNameCosmeticValue(inv);

  return Boolean(
    activeValue &&
      itemValue &&
      activeValue === itemValue
  );
}

function getSeasonalFragmentCount(inv) {
  const item = inv?.item || inv || {};

  return Number(
    item?.accessory_total_items ??
    item?.accessoryTotalItems ??
    item?.total_fragments ??
    item?.totalFragments ??
    inv?.accessory_total_items ??
    inv?.accessoryTotalItems ??
    inv?.total_fragments ??
    inv?.totalFragments ??
    0
  );
}

function getSeasonalItemSeason(inv) {
  const item = inv?.item || inv || {};

  return (
    item?.season_name ??
    item?.seasonName ??
    inv?.season_name ??
    inv?.seasonName ??
    null
  );
}

function SeasonalSkinItemPreview({ inv, user, size = 76 }) {
  const seasonName = getSeasonalItemSeason(inv);

  const rawSide = String(user?.side || "").toLowerCase();

  const side =
    rawSide === "bad" || rawSide === "moon"
      ? "moon"
      : "sun";

  const parts = [
    "body",
    "pants",
    "shirt",
    "boots",
    "neck",
    "head",
    "face",
    "eyes",
    "mouth",
    "weapons",
  ];

  return (
    <div
      style={{
        position: "relative",
        width: size,
        height: size,
        flexShrink: 0,
        margin: "0 auto 10px",
      }}
    >
      {parts.map((part) => {
        const defaultSrc =
          `/assets/${part}/${side}/Common/1_thumb.png`;

        const src = getSkinSrc(
          seasonName,
          part,
          defaultSrc
        );

        return (
          <img
            key={part}
            src={src}
            alt=""
            draggable={false}
            onError={(e) => {
              if (e.currentTarget.src !== defaultSrc) {
                e.currentTarget.src = defaultSrc;
              }
            }}
            style={{
              position: "absolute",
              top: 0,
              left: "50%",
              transform: "translateX(-50%)",
              width: "100%",
              height: "100%",
              objectFit: "contain",
              pointerEvents: "none",
            }}
          />
        );
      })}
    </div>
  );
}

function MiniItem({
  item,
  user,
  onClaim,
  claiming,
}) {
  if (!item) {
    return (
      <div
        style={{
          minHeight: 124,
          borderRadius: 14,
          background: "rgba(255,255,255,0.07)",
          border: "1px solid rgba(255,255,255,0.13)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "rgba(255,255,255,0.65)",
          textShadow: "1px 1px #000",
        }}
      >
        Empty
      </div>
    );
  }

  const cosmetic = isNameCosmetic(item);
  const active = cosmetic && isNameCosmeticActive(item, user);
  const disabled = !cosmetic && item?.claimed;

  const iconStyle = getItemIconStyle(item, 45);

  const itemType = normalizeItemType(item?.item?.type);

const showFullSeasonalSkin =
  itemType === "seasonal_accessory" &&
  getSeasonalFragmentCount(item) === 1;

  let buttonText = "CLAIM";

  if (claiming) {
    buttonText = "...";
  } else if (cosmetic) {
    buttonText = active ? "DISABLE" : "USE IT";
  } else if (item?.claimed) {
    buttonText = "CLAIMED";
  }

  return (
    <div
      onClick={() => {
        if (!disabled && !claiming) {
          onClaim?.(item);
        }
      }}
      role="button"
      tabIndex={0}
      style={{
        backgroundImage: 'url("/v2/item.png")',
        backgroundRepeat: "repeat",
        backgroundSize: "100% 100%",
        backgroundPosition: "center center",
        position: "relative",
        padding: 12,
        paddingBottom: 20,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",

        cursor: disabled || claiming ? "default" : "pointer",

        opacity:
          item?.claimed && !cosmetic
            ? 0.55
            : 1,

        filter: active
          ? "drop-shadow(0 0 6px rgba(87,255,107,0.9))"
          : "none",
      }}
    >
      {active && (
        <div
          className="shadow1"
          style={{
            position: "absolute",
            top: 5,
            right: 5,
            zIndex: 5,
            padding: "2px 5px",
            borderRadius: 5,
            background: "rgba(31,150,50,0.95)",
            color: "#fff",
            fontSize: 10,
            fontWeight: 700,
          }}
        >
          ACTIVE
        </div>
      )}

    {showFullSeasonalSkin ? (
  <SeasonalSkinItemPreview
    inv={item}
    user={user}
    size={76}
  />
) : isNameStyleItem(item) ? (
  <NameStyleSample inv={item} />
) : (
  <img
    src={getItemIconSrc(item)}
    alt={getItemName(item)}
    draggable={false}
    onError={(e) => {
      e.currentTarget.onerror = null;
      e.currentTarget.src = "/pack.png";
    }}
    style={{
      objectFit: "contain",
      filter: "drop-shadow(0 2px 3px rgba(0,0,0,0.7))",
      display: "block",
      ...iconStyle,
      maxWidth: "99%",
    }}
  />
)}
      <div className="claim_bt shadow1">
        {buttonText}
      </div>

      <div
        style={{
          color: "#fff",
          fontSize: 14,
          fontWeight: 500,
          lineHeight: "14px",
          textShadow: "1px 1px #000",
          maxWidth: "100%",
          overflow: "hidden",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          whiteSpace: "normal",
        }}
      >
        {getItemName(item)}
      </div>

      {Number(item.quantity || 0) > 1 && (
        <div
          style={{
            color: "#ffdf5f",
            fontSize: 13,
            marginTop: 3,
          }}
        >
          x{item.quantity}
        </div>
      )}
    </div>
  );
}

export default function InventoryHub({ user, setUser, onNavigate }) {
  const [cards, setCards] = useState([]);
  const [items, setItems] = useState([]);
  const [chests, setChests] = useState([]);
  const [deckCardIds, setDeckCardIds] = useState([]);
  const [deckSize, setDeckSize] = useState(FALLBACK_DECK_SIZE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [claimingItemId, setClaimingItemId] = useState(null);
const [claimError, setClaimError] = useState("");

const [claimModalOpen, setClaimModalOpen] = useState(false);
const [claimTarget, setClaimTarget] = useState(null);

const [claimSelectedCardId, setClaimSelectedCardId] = useState("");
const [claimSelectedCard, setClaimSelectedCard] = useState(null);
const [claimCardPickerOpen, setClaimCardPickerOpen] = useState(false);

const [openedChestModal, setOpenedChestModal] = useState(null);
const [potionResultModal, setPotionResultModal] = useState(null);

  const [characterModalOpen, setCharacterModalOpen] = useState(false);
const [selectedCardId, setSelectedCardId] = useState(null);
const [hubRefreshKey, setHubRefreshKey] = useState(0);

const [hubModalOpen, setHubModalOpen] = useState(false);
const [hubModalScreen, setHubModalScreen] = useState("inventory");
const [inventoryModalTab, setInventoryModalTab] = useState("cards");
const [inventoryModalMergeMode, setInventoryModalMergeMode] = useState(false);

const openInventoryModal = (tab = "cards", mergeMode = false) => {
  setHubModalScreen("inventory");
  setInventoryModalTab(tab);
  setInventoryModalMergeMode(mergeMode);
  setHubModalOpen(true);
};

const openDefenseDeckModal = () => {
  setHubModalScreen("defenseDeck");
  setHubModalOpen(true);
};

const openCardPickerModal = () => {
  setHubModalScreen("cardPicker");
  setHubModalOpen(true);
};

const openCardMergeModal = () => {
  setHubModalScreen("cardMerge");
  setHubModalOpen(true);
};

const openItemsModal = () => {
  setHubModalScreen("items");
  setHubModalOpen(true);
};

const closeHubModal = () => {
  setHubModalOpen(false);
  setInventoryModalMergeMode(false);
  setHubRefreshKey((key) => key + 1);
};
const openCharacterModal = (card) => {
  if (!card?.id) return;
  setSelectedCardId(card.id);
  setCharacterModalOpen(true);
};

const closeCharacterModal = () => {
  setCharacterModalOpen(false);
  setSelectedCardId(null);
};

const needsCardSelection = (inv) => {
  const type = normalizeItemType(inv?.item?.type);

  return (
    type === "ability" ||
    type === "seasonal_accessory" ||
    type === "cardPieces" ||
    type === "evolve"
  );
};
const isChestRow = (inv) => normalizeItemType(inv?.item?.type) === "chest";

const isArenaChestRow = (inv) => {
  return (
    isChestRow(inv) &&
    Boolean(inv?.chest?.id)
  );
};

const isItemChestRow = (inv) => {
  return (
    isChestRow(inv) &&
    !inv?.chest?.id &&
    Boolean(inv?.user_item_id)
  );
};

const isChestLocked = (inv) => {
  const unlockAt = inv?.chest?.unlockAt || inv?.item?.unlock_at;
  if (!unlockAt) return false;
  return new Date() < new Date(unlockAt);
};

const toggleNameCosmetic = async (inv) => {
  if (
    !inv?.user_item_id ||
    claimingItemId
  ) {
    return;
  }

  const active = isNameCosmeticActive(inv, user);
  const action = active ? "remove" : "equip";

  try {
    setClaimingItemId(inv.user_item_id);
    setClaimError("");

    const token = localStorage.getItem("token");

    if (!token) {
      throw new Error("Not logged in.");
    }

    const res = await fetch(
      `${API_BASE}/api/inventory/equip-name-item`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-TG-INITDATA":
            window.Telegram?.WebApp?.initData || "",
        },
        body: JSON.stringify({
          userItemId: inv.user_item_id,
          action,
        }),
      }
    );

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(
        data?.error || "Failed to update cosmetic."
      );
    }

    if (
      data.user &&
      typeof setUser === "function"
    ) {
      setUser((prev) => ({
        ...(prev || {}),
        ...(data.user || {}),
      }));
    }

    setClaimModalOpen(false);
    setClaimTarget(null);
  } catch (err) {
    setClaimError(
      err.message || "Failed to update cosmetic."
    );
  } finally {
    setClaimingItemId(null);
  }
};

const openClaimModal = (inv) => {
  if (!inv || claimingItemId) {
    return;
  }

  if (isNameCosmetic(inv)) {
    toggleNameCosmetic(inv);
    return;
  }

  if (inv.claimed) {
    return;
  }

  setClaimTarget(inv);
  setClaimError("");

  setClaimSelectedCardId("");
  setClaimSelectedCard(null);
  setClaimCardPickerOpen(false);

  setClaimModalOpen(true);
};

const closeClaimModal = () => {
  if (claimingItemId) return;

  setClaimModalOpen(false);
  setClaimTarget(null);
  setClaimError("");

  setClaimSelectedCardId("");
  setClaimSelectedCard(null);
  setClaimCardPickerOpen(false);
};

const claimHubItem = async (inv) => {
  if (!inv || inv.claimed || claimingItemId) return;

  const claimedType = normalizeItemType(
    inv?.item?.type
  );

  const requiresCard = needsCardSelection(inv);

  if (requiresCard && !claimSelectedCardId) {
    setClaimError("Please choose which card to apply this item to.");
    return;
  }

  try {
    const claimKey = inv.user_item_id || `chest:${inv?.chest?.id || ""}`;

    setClaimingItemId(claimKey);
    setClaimError("");

    const token = localStorage.getItem("token");
    if (!token) throw new Error("Not logged in.");

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "X-TG-INITDATA": window.Telegram?.WebApp?.initData || "",
    };

    if (isArenaChestRow(inv)) {
      const chestId = inv?.chest?.id;

      if (!chestId) {
        throw new Error("Missing chest id.");
      }

      if (isChestLocked(inv)) {
        throw new Error("This chest is still locked.");
      }

      const res = await fetch(
        `${API_BASE}/api/arenas/chests/open-guaranteed-sparks`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({ chestId }),
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.error || "Failed to open chest.");
      }

      if (data.user && typeof setUser === "function") {
  setUser((prev) => ({
    ...(prev || {}),
    ...(data.user || {}),
  }));
}

setOpenedChestModal({
  chestName: getItemName(inv),
  chestIcon: getItemIconSrc(inv),
  rewards: mapChestRewardsToRows(data),
});

setChests((prev) =>
  prev.filter((chest) => Number(chest.id) !== Number(chestId))
);
    } else {
      const userItemId = inv.user_item_id;

      if (!userItemId) {
        throw new Error("Missing item identifier.");
      }

      const body = requiresCard ? { card_id: claimSelectedCardId } : {};

      const res = await fetch(
        `${API_BASE}/api/inventory/${encodeURIComponent(userItemId)}/claim`,
        {
          method: "POST",
          headers,
          body: JSON.stringify(body),
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.error || "Failed to claim item.");
      }

      if (claimedType === "magic_potion" && data.magicPotion) {
  setPotionResultModal({
    itemName: getItemName(inv),
    sparksSpent: Number(data.magicPotion.sparksSpent || 0),
    runesReceived: Number(data.magicPotion.runesReceived || 0),
    winningChance: Number(data.magicPotion.winningChance || 0),
    sparksBalance: Number(data.user?.sparks || 0),
    runesBalance: Number(data.user?.runes || 0),
  });
}

if (
  claimedType === "chest" &&
  data.chest
) {
  setOpenedChestModal({
    chestName:
      getItemName(inv),

    chestIcon:
      getItemIconSrc(inv),

    rewards:
      mapChestRewardsToRows(
        data.chest
      ),
  });
}

      setItems((prev) =>
        prev.map((row) =>
          row.user_item_id === userItemId
            ? {
                ...row,
                claimed: true,
                claimed_at:
                  data.claimed_at ||
                  row.claimed_at ||
                  new Date().toISOString(),
              }
            : row
        )
      );

      if (data.user && typeof setUser === "function") {
        setUser((prev) => ({
          ...(prev || {}),
          ...(data.user || {}),
        }));
      }
    }

setClaimModalOpen(false);
setClaimTarget(null);

setClaimSelectedCardId("");
setClaimSelectedCard(null);
setClaimCardPickerOpen(false);

setHubRefreshKey((key) => key + 1);

if (claimedType === "booster") {
  onNavigate?.("home");
}
  } catch (err) {
    setClaimError(err.message || "Failed to claim item.");
  } finally {
    setClaimingItemId(null);
  }
};

  useEffect(() => {
    let cancelled = false;

    async function loadHub() {
      try {
        setLoading(true);
        setError("");

        const token = localStorage.getItem("token");
        if (!token) throw new Error("Not logged in.");

        const headers = {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-TG-INITDATA": window.Telegram?.WebApp?.initData || "",
        };

        const [cardsRes, itemsRes, chestsRes, decksRes, configRes] = await Promise.all([
          fetch(`${API_BASE}/api/shop/mine`, { headers }),
          fetch(`${API_BASE}/api/inventory`, { headers }),
          fetch(`${API_BASE}/api/arenas/chests/all`, { headers }),
          fetch(`${API_BASE}/api/pvp/defense-decks`, { headers }),
          fetch(`${API_BASE}/api/pvp/config`, { headers }),
        ]);

        const [cardsJson, itemsJson, chestsJson, decksJson, configJson] = await Promise.all([
          cardsRes.json().catch(() => ({})),
          itemsRes.json().catch(() => ({})),
          chestsRes.json().catch(() => ({})),
          decksRes.json().catch(() => ({})),
          configRes.json().catch(() => ({})),
        ]);

        if (!cardsRes.ok) throw new Error(cardsJson.error || "Failed to load cards.");
        if (!itemsRes.ok) throw new Error(itemsJson.error || "Failed to load items.");
        if (!chestsRes.ok) throw new Error(chestsJson.error || "Failed to load chests.");

        const nextDeckSize = Number(configJson?.defenseDeckSize || decksJson?.defenseDeckSize || FALLBACK_DECK_SIZE);
        const nextDeckCardIds = Array.isArray(decksJson?.decks?.[OVERALL_DECK_KEY]?.cardIds)
          ? decksJson.decks[OVERALL_DECK_KEY].cardIds.map(Number)
          : [];

        if (!cancelled) {
          setCards(Array.isArray(cardsJson.cards) ? cardsJson.cards : []);
          setItems(Array.isArray(itemsJson.items) ? itemsJson.items : []);
          setChests(Array.isArray(chestsJson.chests) ? chestsJson.chests : []);
          setDeckCardIds(nextDeckCardIds);
          setDeckSize(nextDeckSize > 0 ? nextDeckSize : FALLBACK_DECK_SIZE);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || "Load error.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadHub();
    return () => { cancelled = true; };
  }, [hubRefreshKey]);



const ownedSideCards = useMemo(() => {
  const userSide = getUserVisualSide(user);

  return cards.filter((card) => {
    if (
      card?.is_consumed ||
      card?.merged_into_card_id
    ) {
      return false;
    }

    return getCardSide(card) === userSide;
  });
}, [cards, user]);

const ownedPreviewCards = useMemo(() => {
  return [...ownedSideCards]
    .sort((a, b) => cardPower(b) - cardPower(a))
    .slice(0, PREVIEW_LIMIT);
}, [ownedSideCards]);

  const defensePreviewCards = useMemo(() => {
    return deckCardIds
      .slice(0, deckSize || PREVIEW_LIMIT)
      .map((id) => cards.find((card) => Number(card.id) === Number(id)))
      .slice(0, PREVIEW_LIMIT);
  }, [deckCardIds, deckSize, cards]);

  const itemPreviewRows = useMemo(() => {
    const chestRows = buildChestRows(chests);
    const normalItems = items.filter((inv) => !["runes", "sparks"].includes(String(inv?.item?.type || "").toLowerCase()));

    return [...chestRows, ...normalItems]
      .sort((a, b) => Number(!!a.claimed) - Number(!!b.claimed))
      .slice(0, PREVIEW_LIMIT);
  }, [items, chests]);

  

  return (
  <>
    <div className="shop-container" style={{ paddingTop: 0, paddingBottom: 0 }}>
      <div style={{ width: "100%", maxWidth: 450, margin: "0 auto" }}>
        {loading && (
  <div
    style={{
      minHeight: 430,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <LoadingSpinner text="Loading Inventory" size={64} />
  </div>
)}

        {!loading && error && (
          <div style={{ padding: 16, color: "salmon" }}>
            {error}
          </div>
        )}

        {claimError && (
  <div
    className="shadow1"
    style={{
      color: "#d4c342",
      background: "rgba(0,0,0,0.35)",
      borderRadius: 10,
      fontWeight: 600,
      padding: "8px 10px",
      fontSize: 14,
      textAlign: "center",
    }}
  >
    {claimError}
  </div>
)}

        {!loading && !error && (
          <div style={{ display: "flex", flexDirection: "column", gap: 0}}>

          <SectionCard
  title="Items"
  subtitle={`${items.length + chests.length}`}
  actionLabel="VIEW"
  onClick={openItemsModal}
>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8 }}>
                {Array.from({ length: PREVIEW_LIMIT }).map((_, idx) => (
                 <MiniItem
  key={
    itemPreviewRows[idx]?.user_item_id ||
    `item-empty-${idx}`
  }
  item={itemPreviewRows[idx]}
  user={user}
  onClaim={openClaimModal}
  claiming={
    claimingItemId &&
    claimingItemId ===
      itemPreviewRows[idx]?.user_item_id
  }
/>
                ))}
              </div>
            </SectionCard>

             <SectionCard
  title="Defense Deck"
  subtitle={`${deckCardIds.length}/${deckSize}`}
  actionLabel="EDIT"
  onClick={openDefenseDeckModal}
>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8 }}>
                {Array.from({ length: PREVIEW_LIMIT }).map((_, idx) => (
                  <MiniCard
                    key={defensePreviewCards[idx]?.id || `defense-empty-${idx}`}
                    card={defensePreviewCards[idx]}
                    emptyLabel="Slot"
                    onClick={openCharacterModal}
                  />
                ))}
              </div>
            </SectionCard>


            <SectionCard
  title="Your Cards"
  subtitle={`${ownedSideCards.length}`}
  actionLabel="VIEW"
  onClick={openCardPickerModal}
>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8 }}>
                {Array.from({ length: PREVIEW_LIMIT }).map((_, idx) => (
                  <MiniCard
                    key={ownedPreviewCards[idx]?.id || `owned-empty-${idx}`}
                    card={ownedPreviewCards[idx]}
                    onClick={openCharacterModal}
                  />
                ))}
              </div>
            </SectionCard>

           
            
          </div>
        )}
      </div>
    </div>

    {claimModalOpen && claimTarget && !claimCardPickerOpen && (
  <div className="modal_box">
    <div className="modal_title">
      {isChestRow(claimTarget) ? "Open Chest" : "Confirm Claim"}

      <button
        type="button"
        className="modal_close"
        onClick={closeClaimModal}
      />
    </div>

    <motion.div className="modal_body" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}>
      <div className="modal_inside">
        {normalizeItemType(claimTarget?.item?.type) === "seasonal_accessory" &&
getSeasonalFragmentCount(claimTarget) === 1 ? (
  <SeasonalSkinItemPreview
    inv={claimTarget}
    user={user}
    size={110}
  />
) : (
  <img
    src={getItemIconSrc(claimTarget)}
    alt=""
    style={{
      height: 80,
      objectFit: "contain",
    }}
  />
)}

        <div
          className="shadow2"
          style={{
            fontSize: 28,
            fontWeight: 600,
            marginBottom: 8,
          }}
        >
          {isChestRow(claimTarget) ? "Open Chest?" : "Claim Item?"}
        </div>

        <div
          className="info"
          style={{
            color: "#fff",
            fontSize: 18,
            fontWeight: 600,
            lineHeight: "20px",
          }}
        >
          {getItemName(claimTarget)}
        </div>

        {Number(claimTarget.quantity || 0) > 1 && (
          <div
            className="shadow1"
            style={{
              color: "#ffdf5f",
              fontSize: 20,
              fontWeight: 700,
              marginBottom: 10,
            }}
          >
            x{claimTarget.quantity}
          </div>
        )}

       {needsCardSelection(claimTarget) && (
  <div className="info">
    {claimSelectedCard ? (
      <div
        style={{
          background: "rgba(0,0,0,0.35)",
          border: "1px solid rgba(255,255,255,0.15)",
          borderRadius: 10,
          padding: "10px 12px",
          marginBottom: 10,
        }}
      >
        <div
          style={{
            color: "#fff",
            fontSize: 18,
            fontWeight: 700,
            marginBottom: 3,
          }}
        >
          {claimSelectedCard.name ||
            `Card #${claimSelectedCard.id}`}
        </div>

        <div
          style={{
            color: "#ffdf5f",
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          {claimSelectedCard.rarity || "Common"}
          {" • "}
          Lv {claimSelectedCard.level || 1}
        </div>
      </div>
    ) : (
      <div
        style={{
          color: "rgba(255,255,255,0.7)",
          fontSize: 14,
          marginBottom: 10,
        }}
      >
        No card selected.
      </div>
    )}

    <button
      type="button"
      className="game-button yellow-button"
      disabled={!!claimingItemId}
      onClick={() => {
        setClaimError("");
        setClaimCardPickerOpen(true);
      }}
      style={{
        width: "100%",
        opacity: claimingItemId ? 0.5 : 1,
      }}
    >
      {claimSelectedCard ? "CHANGE CARD" : "SELECT CARD"}
    </button>
  </div>
)}

        {claimError && (
           <div

                  className="info"
                  style={{
                    color: "#d4c342",
                  }}
                >
                 {claimError}
                </div>
        )}
      </div>

      <div className="modal_footer">
        <button
          type="button"
          className="game-button green-button"
          disabled={!!claimingItemId}
          onClick={() => claimHubItem(claimTarget)}
          style={{
            marginRight: 10,
            opacity: claimingItemId ? 0.5 : 1,
            cursor: claimingItemId ? "not-allowed" : "pointer",
          }}
        >
          {claimingItemId
            ? isChestRow(claimTarget)
              ? "Opening..."
              : "Claiming..."
            : isChestRow(claimTarget)
            ? "OPEN"
            : "CLAIM"}
        </button>

        <button
          type="button"
          className="game-button red-button"
          disabled={!!claimingItemId}
          onClick={closeClaimModal}
          style={{
            opacity: claimingItemId ? 0.5 : 1,
            cursor: claimingItemId ? "not-allowed" : "pointer",
          }}
        >
          CANCEL
        </button>
      </div>
        </motion.div>
  </div>
)}

{claimCardPickerOpen && claimTarget && (
  <div className="modal_box">
    <div className="modal_title">
      Select Card

      <button
        type="button"
        className="modal_close"
        onClick={() => {
          setClaimCardPickerOpen(false);
        }}
      />
    </div>

    <CardPicker
      user={user}
      onNavigate={onNavigate}
      openedVia="itemClaim"
      persistSelection={false}
      showBuyButton={false}
      onClose={() => {
        setClaimCardPickerOpen(false);
      }}
      onSelected={({ cardId, card }) => {
        setClaimSelectedCardId(String(cardId));
        setClaimSelectedCard(card);
        setClaimError("");
        setClaimCardPickerOpen(false);
      }}
    />
  </div>
)}

{potionResultModal && (
  <div className="modal_box" style={{ zIndex: 99999 }}>
    <div className="modal_title">
      Potion Complete

      <button type="button" className="modal_close" onClick={() => setPotionResultModal(null)} />
    </div>

    <motion.div className="modal_body" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}>
      <div className="modal_inside">
        <img
          src="/magic_potion.png"
          alt=""
          draggable={false}
          style={{
            height: 110,
            objectFit: "contain",
            marginBottom: 5,
            filter: "drop-shadow(0 3px 5px rgba(0,0,0,0.7))",
          }}
        />

        <div
          className="shadow2"
          style={{
            fontSize: 24,
            fontWeight: 600,
            marginBottom: 5,
          }}
        >
          You Got
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            marginTop: 5,
          }}
        >
          <img
            src="/runes.png"
            alt=""
            draggable={false}
            style={{
              width: 32,
              height: 32,
              objectFit: "contain",
            }}
          />

          <div
            className="shadow2"
            style={{
              color: "#ffdf5f",
              fontSize: 28,
              fontWeight: 600,
            }}
          >
            +{potionResultModal.runesReceived.toLocaleString()} Runes
          </div>
        </div>

        <div className="info">
          <div
            className="shadow1"
            style={{
              color: "#fff",
              fontSize: 16,
              lineHeight: "24px",
              fontWeight: 600,
            }}
          >
            <span style={{ color: "#ffdf5f" }}>
              {potionResultModal.sparksSpent.toLocaleString()} Sparks
            </span>{" "}
            were converted.
          </div>

          
        </div>
      </div>

      <div className="modal_footer">
        <button type="button" className="game-button green-button" onClick={() => setPotionResultModal(null)}>
          AWESOME
        </button>
      </div>
    </motion.div>
  </div>
)}

{openedChestModal && (
  <div
    className="modal_box"
    style={{ zIndex: 99999 }}
  >
    <div className="modal_title">
      Chest Opened

      <button
        type="button"
        className="modal_close"
        onClick={() => setOpenedChestModal(null)}
      />
    </div>

    <motion.div className="modal_body" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}>
      <div className="modal_inside">
        <img
          src="/v2/chest_open.png"
          alt=""
          style={{
            height: 120,
            objectFit: "contain",
            marginTop: -10,
            marginBottom: 8,
            filter: "drop-shadow(0 3px 5px rgba(0,0,0,0.7))",
          }}
        />

        <div
          className="shadow2"
          style={{
            fontSize: 30,
            fontWeight: 700,
            marginBottom: 8,
            textTransform: "uppercase",
          }}
        >
          You Got
        </div>

        <div
          className="shadow1"
          style={{
            color: "#ffdf5f",
            fontSize: 18,
            fontWeight: 700,
            marginBottom: 10,
          }}
        >
          {openedChestModal.chestName}
        </div>

        {openedChestModal.rewards.length > 0 ? (
          <div className="info" style={{ paddingTop: 1 }}>
            {openedChestModal.rewards.map((reward) => (
              <div
                key={reward.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                  padding: "10px 12px",
                  borderRadius: 12,
                  background: "rgba(0,0,0,0.25)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  marginTop: 10,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    minWidth: 0,
                  }}
                >
                  <img
                    src={reward.icon}
                    alt=""
                    style={{
                      width: 38,
                      height: 38,
                      objectFit: "contain",
                    }}
                  />

                  <div
                    style={{
                      textAlign: "left",
                      minWidth: 0,
                    }}
                  >
                    <div
                      className="shadow1"
                      style={{
                        fontSize: 18,
                        color: "#fff",
                        lineHeight: "18px",
                        fontWeight: 700,
                      }}
                    >
                      {reward.label}
                    </div>

                    {reward.extra && (
                      <div
                        className="shadow1"
                        style={{
                          fontSize: 13,
                          color: "rgba(255,255,255,0.82)",
                          marginTop: 2,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: 170,
                        }}
                      >
                        {reward.extra}
                      </div>
                    )}
                  </div>
                </div>

                <div
                  className="shadow1"
                  style={{
                    fontSize: 24,
                    color: "#ffdf5f",
                    fontWeight: 800,
                    whiteSpace: "nowrap",
                  }}
                >
                  +{Number(reward.amount || 0).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="info">
            <div
              className="shadow1"
              style={{
                color: "#fff",
                fontSize: 18,
                fontWeight: 600,
              }}
            >
              Chest opened successfully.
            </div>
          </div>
        )}
      </div>

      <div className="modal_footer">
        <button
          type="button"
          className="game-button green-button"
          onClick={() => setOpenedChestModal(null)}
        >
          COLLECT
        </button>
      </div>
    </motion.div>
  </div>
)}

    {hubModalOpen && (

      <div>



      {hubModalScreen === "items" && (
 <ItemsInventory
  user={user}
  setUser={setUser}
  onNavigate={(screen, params) => {
    setHubModalOpen(false);
    setHubModalScreen("inventory");
    onNavigate?.(screen, params);
  }}
  onClose={() => {
    setHubModalOpen(false);
    setHubModalScreen("inventory");
    setHubRefreshKey((key) => key + 1);
  }}
  onChanged={() => {
    setHubRefreshKey((key) => key + 1);
  }}
/>
)}
  
      {hubModalScreen === "inventory" && (
        <Inventory
          user={user}
          setUser={setUser}
          tab={inventoryModalTab}
          initialTab={inventoryModalTab}
          initialMergeMode={inventoryModalMergeMode}
          setSelectedCardId={setSelectedCardId}
          onNavigate={(screen, params) => {
            if (screen === "inventory") {
              setHubModalScreen("inventory");
              setInventoryModalTab(params?.tab || "cards");
              setInventoryModalMergeMode(false);
              return;
            }

            if (screen === "defenseDeck") {
              setHubModalScreen("defenseDeck");
              return;
            }

            closeHubModal();
            onNavigate?.(screen, params);
          }}
        />
      )}

      {hubModalScreen === "defenseDeck" && (
        <DefenseDeck
  user={user}
  setUser={setUser}
  onDeckSaved={({ cardIds }) => {
    setDeckCardIds(Array.isArray(cardIds) ? cardIds.map(Number) : []);
    setHubRefreshKey((key) => key + 1);
  }}
  onNavigate={(screen, params) => {
    if (screen === "inventory") {
      setHubModalScreen("inventory");
      setInventoryModalTab(params?.tab || "cards");
      setInventoryModalMergeMode(false);
      return;
    }

    if (screen === "defenseDeck") {
      setHubModalScreen("defenseDeck");
      return;
    }

    closeHubModal();
    onNavigate?.(screen, params);
  }}
/>
      )}
  </div>
)}

{hubModalScreen === "cardPicker" && (
  
  <div className="modal_box">
    <div className="modal_title">
      Fighters Desk

      <button
        type="button"
        className="modal_close"
        onClick={() => {
          setHubModalOpen(false);
          setHubModalScreen("inventory");
          setInventoryModalMergeMode(false);
          setHubRefreshKey((key) => key + 1);
        }}
      />
    </div>

    <CardPicker
      user={user}
      setUser={setUser}
      onNavigate={onNavigate}
      openedVia="inventoryHub"
      onOpenMerge={openCardMergeModal}
      onClose={() => {
        setHubModalOpen(false);
        setHubModalScreen("inventory");
        setInventoryModalMergeMode(false);
        setHubRefreshKey((key) => key + 1);
      }}
      onSelected={({ card }) => {
        openCharacterModal(card);
      }}
    />
  </div>
)}

{hubModalScreen === "cardMerge" && (
  <CardMerge
    user={user}
    setUser={setUser}
    onNavigate={(screen, params) => {
      if (screen === "inventoryHub") {
        setHubModalOpen(false);
        setHubModalScreen("inventory");
        setInventoryModalMergeMode(false);
        setHubRefreshKey((key) => key + 1);
        return;
      }

      closeHubModal();
      onNavigate?.(screen, params);
    }}
    onMerged={() => {
      setHubRefreshKey((key) => key + 1);
    }}
  />
)}


{characterModalOpen && selectedCardId && (
  <div className="character-modal-overlay">
    <Character
      user={user}
      setUser={setUser}
      selectedCardId={selectedCardId}
      setSelectedCardId={setSelectedCardId}
      onNavigate={onNavigate}
      openedVia="inventoryHub"
      closeCharacterModal={closeCharacterModal}
    />
  </div>
)}
  </>
); 
}
